<template>
  <section class="panel panel-default" id="progress-otjs-panel" role="region" aria-labelledby="progress-otjs-heading">
    <div class="panel-body">
      <div class="panel-header">
        <h2 id="progress-otjs-heading">
          OTJ Progress
          <span v-if="notificationActionsCount > 0" class="actions-required-pill">
            {{ notificationActionsCount }} {{ plural('action', notificationActionsCount) }} required
          </span>
        </h2>
        <div class="panel-actions">
          <router-link
            :to="{ name: 'programme_otj_page', params: { programmeId: programmeId } }"
            class="btn btn-outlined"
          >
            View Logs
          </router-link>
          <button type="button" @click="openQuickAdd" class="btn btn-primary">Add OTJ Entry</button>
        </div>
      </div>
      <div class="panel-content">
        <ul v-if="notificationActionsCount > 0" class="panel-notification-container" aria-label="OTJ notifications requiring attention">
          <li
            v-for="notification in notificationItems"
            :key="notification.key"
            class="otj-notification-row"
            :class="`otj-notification-row-${notification.tone}`"
          >
            <div class="otj-notification-row-content">
              <i class="far fa-clock-three" aria-hidden="true"></i>
              <span :id="`otj-notification-label-${notification.key}`">{{ notification.label }}</span>
            </div>
            <div class="otj-notification-row-actions">
              <button
                type="button"
                class="otj-link-btn dismiss-btn"
                :aria-describedby="`otj-notification-label-${notification.key}`"
                @click="dismissNotification(notification.key)"
              >
                Dismiss
              </button>
              <button
                type="button"
                class="otj-link-btn review-now-btn"
                :aria-describedby="`otj-notification-label-${notification.key}`"
                @click="reviewNotification(notification)"
              >
                Review Now <i class="fas fa-long-arrow-right" aria-hidden="true"></i>
              </button>
            </div>
          </li>
        </ul>
        <otj-progress-chart :user-id="userId" :programme-id="programmeId"></otj-progress-chart>
      </div>
    </div>
  </section>
</template>

<style scoped>
.panel-header,
.panel-actions {
  display: flex;
  justify-content: space-between;
  gap: 15px;
  align-items: center;
}

.panel-header h2 {
  margin: 0;
  display: flex;
  align-items: center;
  gap: 10px;
}

.panel-content {
  margin-top: 20px;
}

.actions-required-pill {
  border: 1px solid var(--kate-warning);
  color: var(--kate-type-warning);
  border-radius: 999px;
  font-size: 0.75rem;
  line-height: 1;
  padding: 4px 8px;
}

.panel-notification-container {
  list-style: none;
  margin: 0 0 12px;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.otj-notification-row {
  border: 1px solid transparent;
  border-radius: 8px;
  padding: 8px 10px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
}

.otj-notification-row-warning {
  border-color: var(--kate-warning);
  color: var(--kate-type-warning);
  background-color: color-mix(in srgb, var(--kate-warning) 12%, transparent);
}

.otj-notification-row-success {
  border-color: var(--kate-success);
  color: var(--kate-type-success);
  background-color: color-mix(in srgb, var(--kate-success) 12%, transparent);
}

.otj-notification-row-content {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  font-size: 1rem;
}

.otj-notification-row-actions {
  display: inline-flex;
  align-items: center;
  gap: 20px;
}

.otj-link-btn {
  border: 0;
  background: transparent;
  color: inherit;
  padding: 0;
  font-size: 0.875rem;
  text-decoration: none;
}

.otj-link-btn:hover,
.otj-link-btn:focus-visible {
  text-decoration: underline;
}

.otj-link-btn:focus-visible,
.panel-actions .btn:focus-visible {
  outline: 2px solid currentcolor;
  outline-offset: 2px;
}

.dismiss-btn {
  font-family: hk-grotesk-light, sans-serif;
}

.review-now-btn {
  font-family: hk-grotesk-semibold, sans-serif;
}

[data-theme="light"] .actions-required-pill,
[data-theme="light"] .otj-notification-row-warning {
  color: #754600;
}
</style>

<script>
import PluralMixin from '@mixins/plural-mixin';
import useOtjModalStore from '@stores/otj-modal-store';
import useNotificationsStore from '@stores/notifications-store';
import otjProgressChart from '../../programmes/otj_components/otj-progress-chart.vue';

export default {
  components: {
    otjProgressChart,
  },

  mixins: [PluralMixin],

  props: {
    programmeId: {
      type: Number,
      required: true,
    },
    userId: {
      type: Number,
      required: true,
    },
  },

  data() {
    return {
      otjModalStore: useOtjModalStore(),
      notificationsStore: useNotificationsStore(),
      dismissedNotificationKeys: [],
    };
  },

  watch: {
    programmeId(val) {
      if (!val) {
        return;
      }
      this.dismissedNotificationKeys = [];
    },
  },

  computed: {
    notificationItems() {
      return [
        {
          notification_type: 'referred_otj_entry',
          key: 'referred',
          tone: 'warning',
          tab: 'referred',
          count: this.unreadReferredOtjNotifications.length,
          label: this.unreadReferredOtjNotifications.length === 1
            ? '1 OTJ Referred Back'
            : `${this.unreadReferredOtjNotifications.length} OTJs Referred Back`,
        },
        {
          notification_type: 'approved_otj_entry',
          key: 'approved',
          tone: 'success',
          tab: 'approved',
          count: this.unreadApprovedOtjNotifications.length,
          label: this.unreadApprovedOtjNotifications.length === 1
            ? '1 OTJ Approved'
            : `${this.unreadApprovedOtjNotifications.length} OTJs Approved`,
        },
      ].filter(item => item.count > 0);
    },

    notificationActionsCount() {
      return this.unreadApprovedOtjNotifications.length + this.unreadReferredOtjNotifications.length;
    },

    unreadReferredOtjNotifications() {
      return this.filterNotifications('referred_otj_entry');
    },

    unreadApprovedOtjNotifications() {
      return this.filterNotifications('approved_otj_entry');
    },

  },

  methods: {
    filterNotifications(type) {
      return this.notificationsStore.notifications
        .filter(n => n.notification_type === type)
        .filter(n => !n.read_at)
        .filter(n => Number(n.payload?.programme_id) === Number(this.programmeId));
    },

    openQuickAdd() {
      this.otjModalStore.openOtjModal(this.programmeId);
    },

    dismissNotification(notificationKey) {
      if (this.dismissedNotificationKeys.includes(notificationKey)) {
        return;
      }

      this.dismissedNotificationKeys.push(notificationKey);

      const notifications = this.notificationsStore.unreadNotifications
        .filter(n => n.notification_type.includes(notificationKey))
        .filter(n => Number(n.payload?.programme_id) === Number(this.programmeId));

      notifications.forEach(n => this.notificationsStore.markNotificationAsRead(n));
    },

    reviewNotification(notification) {
      this.dismissNotification(notification.key);
      this.$router.push({
        name: 'programme_otj_logs',
        params: { programmeId: this.programmeId },
        query: { tab: notification.tab },
      });
    },
  },
};
</script>
